import React, { useState } from "react";
import { Flex, Spinner, Text } from "@chakra-ui/react";
import { MdReportProblem } from "react-icons/md";
import Card from "horizon/components/card/Card";
import useRefundsPending from "./useRefundsPending";
import RefundsPendingTable from "./RefundsPendingTable";
import RefundDetailDrawer from "./RefundDetailDrawer";
import PaymentPanelHeader from "./PaymentPanelHeader";

/**
 * Builds the header subtitle from the pending refunds count.
 * @param {number} count - Number of blocked refunds
 * @returns {string}
 */
function buildSubtitle(count) {
    if (count <= 0) return "Aucun remboursement bloqué";
    return `${count} paiement${count > 1 ? "s" : ""} nécessite${count > 1 ? "nt" : ""} une action manuelle`;
}

/**
 * Picks the right body based on loading / error / data state.
 * @param {Object} props - { loading, error, refunds, onSelect }
 * @returns {React.ReactElement}
 */
function RefundsBody({ loading, error, refunds, onSelect }) {
    if (loading && (!refunds || refunds.length === 0)) {
        return <Flex justify="center" py="32px"><Spinner color="brand.500" /></Flex>;
    }
    if (error && (!refunds || refunds.length === 0)) {
        return <Text color="red.500" fontSize="sm" py="16px">{typeof error === "string" ? error : "Erreur de chargement"}</Text>;
    }
    return <RefundsPendingTable refunds={refunds} onSelect={onSelect} />;
}

/**
 * Refunds dashboard panel: lists payments stuck in REFUND_MANUAL_REQUIRED.
 * Click a row to open the detail drawer with history + actions.
 * @returns {React.ReactElement}
 */
function RefundsPendingPanel() {
    const { refunds, loading, error, refresh } = useRefundsPending();
    const [selectedId, setSelectedId] = useState(null);
    const onChanged = () => { refresh(); setSelectedId(null); };
    return (
        <Card>
            <PaymentPanelHeader icon={MdReportProblem} iconColor="red.500" title="Remboursements bloqués"
                subtitle={buildSubtitle(refunds?.length || 0)} onRefresh={refresh} refreshing={loading} />
            <RefundsBody loading={loading} error={error} refunds={refunds} onSelect={setSelectedId} />
            <RefundDetailDrawer paymentId={selectedId} refunds={refunds}
                onClose={() => setSelectedId(null)} onChanged={onChanged} />
        </Card>
    );
}

export default RefundsPendingPanel;
